/*
 * Branch lookup component
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2021 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { BranchService } from 'app/shared/services';
import { take } from 'rxjs/operators';
import { Branch } from 'app/shared/models';

@Component ( {
    selector: 'reporter-branch-lookup',
    templateUrl: './branch-lookup.component.html',
    styleUrls: ['./branch-lookup.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BranchLookupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => BranchLookupComponent),
            multi: true
        },
    ],
    standalone: false
} )
export class BranchLookupComponent implements AfterViewInit {

  @Input () incidentId: number;
  @Input () maxlength: number;
  @Input() showLookup = true;
  @Output () branchChange = new EventEmitter<Branch> ();
  searchTerm: string;
  branches: Branch[];
  selected: Branch;

  constructor( private renderer: Renderer2, private elementRef: ElementRef, private branchService: BranchService ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    // eslint-disable-next-line no-prototype-builtins
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit() {
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;

    if ( this.value && !this.selected ) {
      this.selected = { number: this.value, name: this.value };
    }
  }

  writeValue( value: any ): void {
    this.innerValue = value;
    this.onChange ( value );
    this.onTouched ();
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( ( control.value === null || control.value === undefined ) && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    return null;
  }

  search() {
    this.branchService.search ( this.incidentId, this.searchTerm )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.branches = result;
        if ( this.branches.length === 1 ) {
          this.select ( this.branches[ 0 ] );
        }
      } );
  }

  clear() {
    this.value = undefined;
    this.selected = undefined;
    this.branches = undefined;
    this.searchTerm = undefined;
  }

  select( branch: any ) {
    this.selected = branch;
    this.writeValue ( branch.number );
    this.branchChange.emit ( branch );
  }
}
